import { Button, toast } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft, HelpCircle } from "lucide-react";
import { useCallback, useMemo } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  useCreateMeetingMutation,
  useMeetingByIdQuery,
  useUpdateMeetingMutation,
} from "../api/queries";
import {
  MeetingTour,
  meetingFormCreateTourSteps,
  meetingFormEditTourSteps,
  useMeetingTour,
} from "../tour";
import { MeetingForm, type MeetingFormValues } from "../ui/MeetingForm";

export function MeetingFormPage() {
  const params = useParams({ strict: false });
  const id = params?.id as string | undefined;
  const isEdit = Boolean(id);
  const nav = useNavigate();
  const detailQuery = useMeetingByIdQuery(id, isEdit);
  const createMutation = useCreateMeetingMutation();
  const updateMutation = useUpdateMeetingMutation();
  const meeting = detailQuery.data;

  const initialValues = useMemo((): Partial<MeetingFormValues> | undefined => {
    if (!meeting) return undefined;
    return {
      meetingDate: meeting.meetingDate?.slice(0, 10),
      title: meeting.title,
      summary: meeting.summary ?? "",
      attendees: meeting.attendees.map((a) => ({
        userId: a.userId,
        role: a.role ?? "",
      })),
      actions: meeting.actions.map((a) => ({
        description: a.description,
        assigneeUserIds: a.assignees.map((x) => x.userId),
        dueDate: a.dueDate?.slice(0, 10) ?? "",
      })),
    };
  }, [meeting]);

  const handleCancel = useCallback(() => {
    nav({
      to: "/app/management/meetings",
      search: {
        offset: 0,
        limit: 20,
        sort: undefined,
        dateFrom: undefined,
        dateTo: undefined,
        search: undefined,
      },
    });
  }, [nav]);

  const handleSubmit = useCallback(
    async (values: MeetingFormValues) => {
      const payload = {
        meetingDate: values.meetingDate,
        title: values.title.trim(),
        summary: values.summary?.trim() ?? null,
        attendees: values.attendees
          .filter((a) => a.userId)
          .map((a) => ({ userId: a.userId, role: a.role?.trim() || null })),
        actions: values.actions
          .filter((a) => a.description.trim())
          .map((a) => ({
            description: a.description.trim(),
            assigneeIds: a.assigneeUserIds
              .map((id) => id.trim())
              .filter(Boolean),
            dueDate: a.dueDate?.trim() || null,
          })),
      };

      if (isEdit && id) {
        updateMutation.mutate(
          { id, dto: payload },
          {
            onSuccess: () => {
              toast.success("ບັນທຶກການປະຊຸມອັບເດດແລ້ວ");
              nav({ to: "/app/management/meetings/$id", params: { id } });
            },
            onError: () => toast.error("ບໍ່ສາມາດບັນທຶກໄດ້"),
          },
        );
      } else {
        createMutation.mutate(payload, {
          onSuccess: (data) => {
            toast.success("ສ້າງບັນທຶກການປະຊຸມແລ້ວ");
            nav({
              to: "/app/management/meetings/$id",
              params: { id: data.id },
            });
          },
          onError: () => toast.error("ບໍ່ສາມາດສ້າງໄດ້"),
        });
      }
    },
    [isEdit, id, createMutation, updateMutation, nav],
  );

  const tour = useMeetingTour(isEdit ? "formEdit" : "formCreate");
  const formTourSteps = isEdit
    ? meetingFormEditTourSteps
    : meetingFormCreateTourSteps;

  if (isEdit && detailQuery.isLoading) {
    return (
      <>
        <Header />
        <Main>
          <div className="mx-auto max-w-3xl py-8 text-muted-foreground">
            ກຳລັງໂຫຼດ...
          </div>
        </Main>
      </>
    );
  }

  if (isEdit && !detailQuery.isLoading && !meeting) {
    return (
      <>
        <Header />
        <Main>
          <div className="mx-auto max-w-3xl py-8 text-muted-foreground">
            ບໍ່ພົບບັນທຶກການປະຊຸມ
          </div>
        </Main>
      </>
    );
  }

  const title = isEdit ? "ແກ້ໄຂບັນທຶກການປະຊຸມ" : "ສ້າງບັນທຶກການປະຊຸມ";
  const subtitle = isEdit
    ? "ແກ້ໄຂວັນທີ ຫົວຂໍ້ ສະຫຼຸບ ຜູ້ຮ່ວມ ແລະ ປະເດັນທີ່ລົມກັນ"
    : "ປ້ອນວັນທີ ຫົວຂໍ້ ສະຫຼຸບ ຜູ້ຮ່ວມ ແລະ ປະເດັນທີ່ລົມກັນ";

  return (
    <>
      <Header />
      <Main>
        <MeetingTour
          steps={formTourSteps}
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        <div
          className="mb-6 flex items-center justify-between gap-2"
          data-tourid={
            isEdit ? "meeting-form-edit-back" : "meeting-form-create-back"
          }
        >
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-semibold text-2xl">{title}</h1>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 shrink-0 rounded-full"
                onClick={tour.startTour}
                title="ເລີ່ມທົດສອບການນຳທາງ"
                aria-label="ເລີ່ມທົດສອບການນຳທາງ"
              >
                <HelpCircle className="size-5" />
              </Button>
            </div>
            <p className="text-muted-foreground text-sm">{subtitle}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleCancel}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              ກັບຄືນ
            </Button>
          </div>
        </div>
        <div
          className="mx-auto max-w-3xl space-y-6"
          data-tourid="meeting-form-form"
        >
          <MeetingForm
            mode={isEdit ? "edit" : "create"}
            initialValues={isEdit ? initialValues : undefined}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isSubmitting={createMutation.isPending || updateMutation.isPending}
          />
        </div>
      </Main>
    </>
  );
}
